import { Col, Row } from "react-bootstrap";
import { Input } from "../Input";
import { SellingStrategyOption, SellingStrategyPicker } from "./SellingStrategyPicker";
import { useTranslation } from "react-i18next";
import { CurrencyPicker } from "../CurrencyPicker";
import React from "react";
import { DiscountSellingStrategy } from "./DiscountSellingStrategy";
import { CampaignCGRPSellingStrategy } from "./CampaignCGRPSellingStrategy";
import { SpotCGRPSellingStrategy } from "./SpotCGRPSellingStrategy";
import { DefaultInput } from "../DefaultInput";
import { SpotCountSellingStrategy } from "./SpotCountSellingStrategy";
import { strategyHasBudget } from "../../../Business/utils/BriefUtils";
import { SellingStrategyId } from "../../../Business/dto/BriefJAM";
import { ApiClientManager } from "../../../Business/api/ApiClientManager";
import { SellingStrategyApiClient } from "../../../Business/api/SellingStrategyApiClient";

interface SellingStrategyProps {
    setValues: (value: any | any[], key: string | string[], isUserSelection?: boolean) => void;
    inputValues: any;
    overrideDiscountRate?: boolean;
    onOverrideDiscountRateClick?: (value: boolean) => void;
    externalUser: boolean;
}

export const SellingStrategyComponent = (props: SellingStrategyProps) => {
    const { t } = useTranslation();
    const [availableSellingStrategies, setAvailableSellingStrategies] = React.useState<SellingStrategyId[]>([])

    React.useEffect(() => {
        // get sellingStrategies
        ApiClientManager.getInstance().getApiClient<SellingStrategyApiClient>(SellingStrategyApiClient.key).GetSellingStrategies()
        .then((res) => {
            setAvailableSellingStrategies(res)
        })
    }, [])

    React.useEffect(() => {
        const values: any[] = []
        const keys: string[] = []

        if (props.inputValues.sellingStrategyType) {
            const hasDefaultDiscount = props.inputValues.sellingStrategyType.id === "discount" || props.inputValues.sellingStrategyType.id === "costgrp-campaign" || props.inputValues.sellingStrategyType.id === "spotcount"
            
            // discount rate
            if ( hasDefaultDiscount ) values.push(props.inputValues.defaultDiscount)
            else values.push(0)
            keys.push("discountRate")

            // budget
            if (props.inputValues.sellingStrategyType.id === "spotcount") {
                values.push(0)
                keys.push("fullBudget")
            }

            // hasBudget
            values.push(strategyHasBudget.includes(props.inputValues.sellingStrategyType?.id))
            keys.push("hasBudget")
            
            // sellingStrategyValue
            values.push(0)
            keys.push("sellingStrategyValue")

            props.setValues(values, keys)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.inputValues.sellingStrategyType])

    const SellingStrategyInputs = () => {
        const sellingStrategy = props.inputValues.sellingStrategyType as SellingStrategyOption;
        switch (sellingStrategy.id) {
            case "discount":
                if (props.overrideDiscountRate === undefined || props.onOverrideDiscountRateClick === undefined)
                    return <></>

                return <DiscountSellingStrategy
                    setValues={props.setValues}
                    inputValues={props.inputValues}
                    overrideDiscountRate={props.overrideDiscountRate}
                    onOverrideDiscountRateClick={props.onOverrideDiscountRateClick}
                />
            case "costgrp-campaign":
                if (props.overrideDiscountRate === undefined || props.onOverrideDiscountRateClick === undefined)
                    return <></>

                return <CampaignCGRPSellingStrategy
                    setValues={props.setValues}
                    inputValues={props.inputValues}
                    overrideDiscountRate={props.overrideDiscountRate}
                    onOverrideDiscountRateClick={props.onOverrideDiscountRateClick}
                />
            case "costgrp-spot":
                return <SpotCGRPSellingStrategy
                    setValues={props.setValues}
                    inputValues={props.inputValues}
                />
            case "spotcount":
                if (props.overrideDiscountRate === undefined || props.onOverrideDiscountRateClick === undefined)
                    return <></>

                return <SpotCountSellingStrategy
                    setValues={props.setValues}
                    inputValues={props.inputValues}
                    overrideDiscountRate={props.overrideDiscountRate}
                    onOverrideDiscountRateClick={props.onOverrideDiscountRateClick}
                />
        }

        return <></>
    }

    return (
        <>
            <Row className="briefInputRow">
                <Col className="multipleInputsRow">
                    <Input required title={t("txt_currency")}>
                        <CurrencyPicker
                            required
                            setSelectedCurrency={(currency: any) => props.setValues(currency, "selectedCurrency", true)}
                            value={props.inputValues.selectedCurrency}
                        />
                    </Input>
                </Col>
                <Col className="multipleInputsRow">
                    {!props.externalUser ?
                        <Input required title={t("txt_selling_strategy")}>
                            <SellingStrategyPicker
                                setSelectedSellingStrategy={(sellingStrategy: any) => props.setValues(sellingStrategy, "sellingStrategyType", true)}
                                required
                                availableSellingStrategies={availableSellingStrategies}
                                value={props.inputValues.sellingStrategyType}
                            />
                        </Input>
                        :
                        <Input required title={t("txt_net_budget")}>
                            <DefaultInput
                                onChange={(value: any) => props.setValues(parseFloat(value), "fullBudget", true)}
                                type="currency"
                                iconKey="currency"
                                placeholder={t("txt_net_budget")}
                                value={props.inputValues.fullBudget}
                                params={{numberMaxValue: 9999999999, numberIsInterger: true}}
                            />
                        </Input>
                    }
                </Col>
            </Row>
            <Row className="briefInputRow">
                {props.inputValues.sellingStrategyType &&
                    SellingStrategyInputs()
                }
            </Row>
        </>
    );
}